import {
  IncreaseExternalAccountDetails,
  CreateFreelancerPaymentRequest,
  CreateFreelancerPaymentResponse,
  CancelFreelancerPaymentRequest,
  CancelFreelancerPaymentResponse,
  ApproveFreelancerPaymentRequest,
  ApproveFreelancerPaymentResponse,
  CreateTestPaymentRequest,
  CreateTestPaymentResponse,
  CheckTestPaymentStatusQuery,
  CheckTestPaymentStatusResponse,
  GetRecentTransactionsQuery,
  GetRecentTransactionsResponse,
  GetTransactionSummaryQuery,
  ResubmitAchPrenotificationRequest,
  ResubmitAchPrenotificationResponse,
  CreatePaymentProfileRequest,
  CreatePaymentProfileResponse,
  UpdatePaymentProfileRequest,
  UpdatePaymentProfileResponse,
  GetPaymentProfileResponse,
  PayStakeholderInvoiceRequest,
  PayStakeholderInvoiceResponse,
  AccountBalancesResponse,
  GetTransactionSummaryResponse,
} from "@freetech/models/bank";
import { encryptPayload } from "@/core/security/encryptPayload";
import { axiosClient } from "../axiosClient";

export const bankFunctions = {
  admin: {
    createExternalAccountOutboundTestPayment: async (
      userId: string,
      amount: number,
      memo: string,
      paymentDate: string,
      direction: "outbound" | "inbound"
    ): Promise<CreateTestPaymentResponse> => {
      const requestBody: CreateTestPaymentRequest = {
        userId,
        amount,
        memo,
        paymentDate,
        direction,
      };
      return await axiosClient.post("/bank/admin/freelancer/create-test-payment", requestBody);
    },
    createFreelancerPayment: async (body: CreateFreelancerPaymentRequest): Promise<CreateFreelancerPaymentResponse> => {
      return await axiosClient.post("/bank/admin/freelancer/create-payment", body);
    },
    /**
     * @deprecated
     */
    resubmitAchPrenotification: async (userId: string): Promise<ResubmitAchPrenotificationResponse> => {
      const requestBody: ResubmitAchPrenotificationRequest = {
        userId,
      };
      return await axiosClient.post("/bank/admin/resubmit-ach-prenotification", requestBody);
    },
    getAccountBalances: async (): Promise<AccountBalancesResponse> => {
      return await axiosClient.get("/bank/admin/account/balances");
    },
    getRecentTransactions: async (params: GetRecentTransactionsQuery = {}): Promise<GetRecentTransactionsResponse> => {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.timeRange) queryParams.append("timeRange", params.timeRange);
      if (params.transactionId) queryParams.append("transactionId", params.transactionId);

      const queryString = queryParams.toString();
      const url = `/bank/admin/recent/transactions${queryString ? `?${queryString}` : ""}`;

      return await axiosClient.get(url);
    },
    cancelFreelancerPayment: async (achTransferId: string): Promise<CancelFreelancerPaymentResponse> => {
      const requestBody: CancelFreelancerPaymentRequest = {
        achTransferId,
      };
      return await axiosClient.post("/bank/admin/freelancer/cancel-payment", requestBody);
    },
    approveFreelancerPayment: async (achTransferId: string): Promise<ApproveFreelancerPaymentResponse> => {
      const requestBody: ApproveFreelancerPaymentRequest = {
        achTransferId,
      };
      return await axiosClient.post("/bank/admin/freelancer/approve-payment", requestBody);
    },
    checkTestPaymentStatus: async (userId: string): Promise<CheckTestPaymentStatusResponse> => {
      const params: CheckTestPaymentStatusQuery = { userId };
      return await axiosClient.get("/bank/admin/freelancer/check-status-test-payment", { params });
    },
    getTransactionSummary: async (params: GetTransactionSummaryQuery = {}): Promise<GetTransactionSummaryResponse> => {
      const queryParams = new URLSearchParams();
      if (params.timeRange) queryParams.append("timeRange", params.timeRange);

      const queryString = queryParams.toString();
      const url = `/bank/admin/transaction/summary${queryString ? `?${queryString}` : ""}`;

      return await axiosClient.get(url);
    },
  },

  freelancer: {
    createFreelancerPaymentProfile: async (
      privatePaymentProfile: Partial<IncreaseExternalAccountDetails> & {
        nickname: string;
      }
    ): Promise<CreatePaymentProfileResponse> => {
      const encryptedPayload = await encryptPayload(privatePaymentProfile);
      const requestBody: CreatePaymentProfileRequest = {
        encryptedPayload,
      };
      return await axiosClient.post("/bank/freelancer/create-payment-profile", requestBody);
    },
    getMyFreelancerPaymentProfile: async (): Promise<GetPaymentProfileResponse> => {
      return await axiosClient.get("/bank/freelancer/payment-profile");
    },
    updateFreelancerPaymentProfile: async (
      privatePaymentProfile: Partial<IncreaseExternalAccountDetails> & {
        nickname?: string;
      }
    ): Promise<UpdatePaymentProfileResponse> => {
      const encryptedPayload = await encryptPayload(privatePaymentProfile);
      const requestBody: UpdatePaymentProfileRequest = {
        encryptedPayload,
      };
      return await axiosClient.post("/bank/freelancer/update-payment-profile", requestBody);
    },
  },

  stakeholder: {
    createStakeholderPaymentProfile: async (
      privatePaymentProfile: Partial<IncreaseExternalAccountDetails> & {
        nickname: string;
      }
    ): Promise<CreatePaymentProfileResponse> => {
      const encryptedPayload = await encryptPayload(privatePaymentProfile);
      const requestBody: CreatePaymentProfileRequest = {
        encryptedPayload,
      };
      return await axiosClient.post("/bank/stakeholder/create-payment-profile", requestBody);
    },
    getMyStakeholderPaymentProfile: async (): Promise<GetPaymentProfileResponse> => {
      return await axiosClient.get("/bank/stakeholder/payment-profile");
    },
    payMyStakeholderInvoice: async (
      clientId: string,
      invoiceId: string,
      amount?: number
    ): Promise<PayStakeholderInvoiceResponse> => {
      const requestBody: PayStakeholderInvoiceRequest = {
        clientId,
        invoiceId,
        amount,
      };
      return await axiosClient.post("/bank/stakeholder/pay-invoice", requestBody);
    },
    updateStakeholderPaymentProfile: async (
      privatePaymentProfile: Partial<IncreaseExternalAccountDetails> & {
        nickname?: string;
      }
    ): Promise<UpdatePaymentProfileResponse> => {
      const encryptedPayload = await encryptPayload(privatePaymentProfile);
      const requestBody: UpdatePaymentProfileRequest = {
        encryptedPayload,
      };
      return await axiosClient.post("/bank/stakeholder/update-payment-profile", requestBody);
    },
  },
};
